import SparkMD5 from 'spark-md5';

import { AbortError, ConfigError } from '../errors/errors';
import DataAdminServiceFactory from './DataAdminServiceFactory';
import { FileUploadStatus } from '../models/FileUploadTypes';
import IDataAdminService from './IDataAdminService';
import { logEvent, logError } from '../utils/trackingUtils';

export interface CompletedPart {
  ETag: string;
  PartNumber: number;
}

interface PartUploadResult {
  partNumber: number;
  etag: string;
}

export interface PresignedPostData {
  uploadUrl: string;
  uploadFields: Record<string, string>;
}

export interface ManagedUploadResult {
  completedParts: CompletedPart[];
  totalRetriesAttempted: number;
}

/**
 * Client-side upload helper for web applications
 */
export class ClientMultipartUploader {
  private getAccessToken: () => Promise<string>;

  private abortController: AbortController;
  private onProgress: (chunkBytesLoaded: number, uploadStatus: FileUploadStatus) => void;
  private onConcurrencyChange: (activeUploads: number) => void;
  private onUploadFailure: (attempt: number, error: unknown, status: FileUploadStatus) => void;

  private siteCode: string;
  private dataAdminService: IDataAdminService;
  private caseFolder: string;
  private concurrency: number;
  private maxRetries: number;
  isUploading = false;

  constructor(
    env: string,
    getAccessToken: () => Promise<string>, 
    siteCode: string,
    caseFolder: string,
    onProgressCallback: (chunkBytesLoaded: number, uploadStatus: FileUploadStatus) => void,
    onConcurrencyChangeCallback: (activeUploads: number) => void,
    onUploadFailureCallback: (attempt: number, error: unknown, status: FileUploadStatus) => void,
    concurrency: number,
    maxRetries: number,
  ) {
    this.caseFolder = caseFolder;
    this.getAccessToken = getAccessToken;
    this.abortController = new AbortController();
    this.concurrency = concurrency;
    this.maxRetries = Math.max(maxRetries, 0);
    this.onProgress = onProgressCallback;
    this.onConcurrencyChange = onConcurrencyChangeCallback;
    this.onUploadFailure = onUploadFailureCallback;
    this.siteCode = siteCode;
    this.dataAdminService = new DataAdminServiceFactory().create(env);
  }

  cancel(): void {
    if (this.isUploading) {
      this.abortController.abort();
    }
  }

  checkCancelled(): void {
    if (this.abortController.signal.aborted) {
      this.isUploading = false;
      throw new AbortError('Upload cancelled.');
    }
  }

  setConcurrency(concurrency: number): void {
    this.concurrency = concurrency;
  }

  setMaxRetries(maxRetries: number): void {
    this.maxRetries = maxRetries;
  }

  async managedUpload(
    file: File,
    objectKey: string,
    chunkSizeOverride: number | null
  ) : Promise<ManagedUploadResult> {
    let completedParts: CompletedPart[] = [];
    let totalRetriesAttempted = 0;

    const fileSize = file.size || 0;
    try {
      // Update file state to INITIALIZING
      this.onProgress(0, FileUploadStatus.INITIALIZING);

      let uploadId: string = '';
      let partSize: number = 0;

      // Initiate file upload, with retry.
      for (let attempt = 0; attempt < this.maxRetries + 1; attempt++) {
        try {
          const initResults = await this.dataAdminService.initFileUpload(
            await this.getAccessToken(), this.siteCode, this.caseFolder, objectKey, fileSize
          );
          partSize = initResults.partSize;
          uploadId = initResults.uploadId;
          logEvent({
            action: 'file_upload.init',
            siteCode: this.siteCode,
            caseFolder: this.caseFolder,
            objectKey,
            sizeBytes: fileSize,
            partSize
          });
          break;
        } catch (error) {
          console.error(`Attempt ${attempt + 1} failed during upload initiation:`, error);
          logError('file_upload.init_error', error);
          
          if (error instanceof ConfigError || (attempt === (this.maxRetries))) {
            this.onUploadFailure(attempt, error, FileUploadStatus.FAILED);
            throw error; // Do not retry on fatal errors
          }
          this.onUploadFailure(attempt, error, FileUploadStatus.RETRYING);

          // Retru with exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          totalRetriesAttempted ++;
        }
      };
      
      // Upload all file parts.
      completedParts = await this.uploadFileInParts(
        file,
        objectKey,
        chunkSizeOverride || partSize,
        uploadId
      );

      // Complete the upload
      for (let attempt = 0; attempt < this.maxRetries + 1; attempt++) {
        try {
          await this.dataAdminService.completeMultipartUpload(
            await this.getAccessToken(), this.siteCode, this.caseFolder, objectKey, uploadId, completedParts
          );
          logEvent({
            action: 'file_upload.complete',
            siteCode: this.siteCode,
            caseFolder: this.caseFolder,
            objectKey,
            partCount: completedParts.length
          });
          break; // Success
        } catch (error) {
          console.error(`Attempt ${attempt + 1} failed during upload completion:`, error);
          logError('file_upload.complete_error', error);
          
          if (error instanceof ConfigError || (attempt === (this.maxRetries))) {
            this.onUploadFailure(attempt, error, FileUploadStatus.FAILED);
            throw error; // Do not retry on fatal errors
          }
          this.onUploadFailure(attempt, error, FileUploadStatus.RETRYING);

          // Retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          totalRetriesAttempted ++;
        }
      };

      // Signals the caller that upload is complete.
      this.onProgress(0, FileUploadStatus.COMPLETED);

    } catch (error) {
      // On any fatal error from any thread, cancel any other threads in progress.
      this.cancel();
      throw error; // Allow caller to handle the fatal error.
    }

    return {
      completedParts,
      totalRetriesAttempted: totalRetriesAttempted
    };
  }

  /**
   * Upload file in chunks with retry logic
   */
  async uploadFileInParts(
    file: File,
    objectKey: string,
    partSize: number,
    uploadId: string
  ): Promise<CompletedPart[]> {
    const totalParts = Math.ceil(file.size / partSize);
    const completedParts: CompletedPart[] = [];

    console.log(`Starting upload of ${file.name} (${file.size} bytes) in ${totalParts} parts`);
    this.isUploading = true;

    // Track active uploads
    const activeUploads = new Set<Promise<void>>();
    let partNumber = 1;

    // Function to start upload of a part
    const startUpload = (partNum: number): Promise<void> => {
      const uploadPromise = this.uploadPartWithRetry(
        completedParts,
        file,
        objectKey,
        partNum,
        partSize,
        uploadId
      ).finally(() => {
        // Remove from active uploads when complete
        activeUploads.delete(uploadPromise);
        this.onConcurrencyChange(activeUploads.size);
      });

      activeUploads.add(uploadPromise);
      this.onConcurrencyChange(activeUploads.size);
      return uploadPromise;
    };

    // Start initial batch up to concurrency limit
    while (partNumber <= totalParts && activeUploads.size < this.concurrency) {
      startUpload(partNumber++);
    }

    // Continue until all parts are processed
    while (partNumber <= totalParts || activeUploads.size > 0) {
      this.checkCancelled();

      if (activeUploads.size > 0) {
        // Wait for any upload to complete
        await Promise.race(activeUploads);

        // Start new upload if there are more parts to process
        // Add threads up to concurrency limit (accounts for changes in concurrency)
        while (partNumber <= totalParts && activeUploads.size < this.concurrency) {
          startUpload(partNumber++);
        }
      }
    }

    this.isUploading = false;
    return completedParts.sort((a, b) => (a.PartNumber || 0) - (b.PartNumber || 0));
  }

  private async uploadPartWithRetry(
    completedParts: CompletedPart[],
    file: File,
    objectKey: string,
    partNumber: number,
    partSize: number,
    uploadId: string
  ): Promise<void> {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const chunk = file.slice(start, end);

    for (let attempt = 0; attempt < this.maxRetries + 1; attempt++) {
      try {
        const checksum = await this.calculateChecksum(chunk);
        const presignedUrl = await this.dataAdminService.getMultiPartPresignedUrl(
          await this.getAccessToken(), 
          this.siteCode, 
          this.caseFolder,
          partNumber,
          objectKey,
          uploadId,
          checksum
        );

        this.checkCancelled(); // Check if cancelled before initiating upload of this part.
        const result = await this.uploadPart(presignedUrl, partNumber, chunk, checksum);

        completedParts.push({
          PartNumber: result.partNumber,
          ETag: result.etag,
        });

        console.log(`Part ${partNumber} uploaded successfully for object [${objectKey}].`);
        logEvent({
          action: 'file_upload.part',
          siteCode: this.siteCode,
          caseFolder: this.caseFolder,
          objectKey,
          partNumber,
          chunkBytes: chunk.size
        });
        this.onProgress(chunk.size, FileUploadStatus.UPLOADING);
        return;
      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed for part ${partNumber}:`, error);
        logError('file_upload.part_error', error);
        this.checkCancelled(); // If cancelled ignore rest of error handling.
        
        if (error instanceof ConfigError || (attempt === this.maxRetries)) {
          // Cancel the entire file upload on fatal errors or max retries reached for any part.
          this.onUploadFailure(attempt, error, FileUploadStatus.FAILED);
          this.cancel(); 
          throw error; // Do not retry on fatal errors
        }
        this.onUploadFailure(attempt, error, FileUploadStatus.RETRYING);

        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  /**
   * Calculates MD5 checksum for a blob
   */
  async calculateChecksum(data: Blob): Promise<string> {
    const arrayBuffer = await data.arrayBuffer();
    // Hex MD5
    const md5Hex = SparkMD5.ArrayBuffer.hash(arrayBuffer);
    // Convert hex -> bytes -> Base64
    const match = md5Hex.match(/.{2}/g);
    if (!match) {
      throw new Error('Failed to compute MD5 checksum.');
    }
    const byteArray = Uint8Array.from(match.map((h: string) => parseInt(h, 16)));
    let binary = '';
    for (let i = 0; i < byteArray.length; i++) binary += String.fromCharCode(byteArray[i]);
    const md5Base64 = btoa(binary);
    return md5Base64;
  }

  /**
   * Upload a file part using pre-signed PUT
   */
  async uploadPart(
    presignedUrl: string,
    partNumber: number,
    fileChunk: Blob,
    checksum: string
  ): Promise<PartUploadResult> {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileChunk,
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-MD5': checksum, // AWS will validate the checksum vs the file content.
        },
        signal: this.abortController.signal // This would abort the fetch request in progress if needed.
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Part ${partNumber} upload failed: ${response.status} - ${errorText}`);
      }

      // Extract ETag from response headers
      const etag = response.headers.get('ETag');
      if (!etag) {
        throw new ConfigError(`No ETag returned for part ${partNumber}; S3 may not be configured correctly.`);
      }

      return {
        partNumber,
        etag: etag
      };
    } catch (error) {
      console.error(`Error uploading part ${partNumber}:`, error);
      throw error;
    }
  }
}
